<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';
import { buildReviewActions } from './utils/automationOperationsConstants';
import { actionKey } from './utils/automationOperationsFormatters';

const props = defineProps({
  deadLetters: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  meta: { type: Object, default: () => ({}) },
  selectedIds: { type: Array, default: () => [] },
  allVisibleSelected: { type: Boolean, default: false },
  hasDeadLetters: { type: Boolean, default: false },
  canOperate: { type: Boolean, default: false },
  actionLoading: { type: Object, default: () => ({}) },
});

defineEmits([
  'toggleVisibleSelection',
  'toggleSelection',
  'transition',
  'openDetails',
  'pageChange',
]);

const { t } = useI18n();
const { deadLetterState, eventLabel, formatDate, formatStatus, labelColor } =
  useAutomationOperationsFormatters();

const dlqHeaders = computed(() => [
  { key: 'selected', label: '', align: 'start' },
  t('AUTOMATION.OPERATIONS.DLQ.TABLE_HEADER.EVENT'),
  t('AUTOMATION.OPERATIONS.DLQ.TABLE_HEADER.JOB'),
  t('AUTOMATION.OPERATIONS.DLQ.TABLE_HEADER.ERROR'),
  t('AUTOMATION.OPERATIONS.DLQ.TABLE_HEADER.REVIEW'),
  t('AUTOMATION.OPERATIONS.DLQ.TABLE_HEADER.ACTIONS'),
]);

const reviewActions = computed(() => buildReviewActions(t));

const isSelected = id => props.selectedIds.includes(id);
const isActionLoading = (id, actionName) =>
  Boolean(props.actionLoading[actionKey(id, actionName)]);
</script>

<template>
  <BaseTable
    :headers="dlqHeaders"
    :items="deadLetters"
    :loading="isLoading"
    :loading-message="$t('AUTOMATION.OPERATIONS.DLQ.LOADING')"
    :no-data-message="$t('AUTOMATION.OPERATIONS.DLQ.EMPTY')"
  >
    <template #header-0>
      <input
        type="checkbox"
        class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary-50"
        :aria-label="$t('AUTOMATION.OPERATIONS.DLQ.SELECT_ALL')"
        :checked="allVisibleSelected"
        :disabled="!hasDeadLetters"
        @change="$emit('toggleVisibleSelection')"
      />
    </template>
    <template #row="{ items }">
      <template v-for="deadLetter in items" :key="deadLetter.id">
        <BaseTableRow :item="deadLetter" :state="deadLetterState(deadLetter)">
          <template #default>
            <BaseTableCell class="w-10">
              <input
                type="checkbox"
                class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary-50"
                :aria-label="
                  $t('AUTOMATION.OPERATIONS.DLQ.SELECT_ROW', {
                    id: deadLetter.id,
                  })
                "
                :checked="isSelected(deadLetter.id)"
                @change="$emit('toggleSelection', deadLetter.id)"
              />
            </BaseTableCell>
            <BaseTableCell class="min-w-44">
              <div class="flex min-w-0 flex-col gap-1">
                <div class="flex items-center gap-2">
                  <span class="text-body-main text-n-slate-12">
                    {{ eventLabel(deadLetter.id) }}
                  </span>
                  <WootLabel
                    :label="formatStatus(deadLetter.status)"
                    :color="labelColor(deadLetter.status)"
                    compact
                  />
                </div>
                <span class="text-body-main text-n-slate-11">
                  {{ formatDate(deadLetter.created_at) }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell class="min-w-48">
              <div class="flex min-w-0 flex-col gap-1">
                <span class="truncate text-body-main text-n-slate-12">
                  {{ deadLetter.job_class || '-' }}
                </span>
                <span class="truncate text-body-main text-n-slate-11">
                  {{ deadLetter.queue || '-' }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell class="min-w-44">
              <span class="truncate text-body-main text-n-slate-11">
                {{ deadLetter.error_class || '-' }}
              </span>
            </BaseTableCell>
            <BaseTableCell class="min-w-56">
              <div class="flex min-w-0 flex-col gap-1">
                <span class="text-body-main text-n-slate-12">
                  {{ formatStatus(deadLetter.review_action) }}
                </span>
                <span class="line-clamp-2 text-body-main text-n-slate-11">
                  {{ deadLetter.review_reason || '-' }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell align="end" class="min-w-48">
              <div class="flex justify-end gap-1">
                <Button
                  v-for="action in reviewActions"
                  :key="action.name"
                  v-tooltip.top="action.label"
                  :icon="action.icon"
                  :color="action.color"
                  variant="ghost"
                  size="sm"
                  :is-loading="isActionLoading(deadLetter.id, action.name)"
                  :disabled="!canOperate"
                  :data-test="`dlq-action-${action.name}-${deadLetter.id}`"
                  @click="$emit('transition', deadLetter, action.name)"
                />
                <Button
                  v-tooltip.top="$t('AUTOMATION.OPERATIONS.DLQ.DETAILS')"
                  icon="i-lucide-panel-right-open"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  :is-loading="isActionLoading(deadLetter.id, 'details')"
                  :data-test="`dlq-details-${deadLetter.id}`"
                  @click="$emit('openDetails', deadLetter)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </template>
  </BaseTable>
  <PaginationFooter
    v-if="meta.total_count"
    :current-page="Number(meta.page)"
    :total-items="Number(meta.total_count)"
    :items-per-page="Number(meta.per_page)"
    class="!px-0"
    @update:current-page="$emit('pageChange', $event)"
  />
</template>
